// Full-document render of the tempurpedic ProductListPage: PageSkeleton
// wrapping the body in order (normal site chrome; no body_class / style
// overrides):
//
// 1. The hero (rendered only when the page has a hero_image): width-1600
//    rendition inline background, the badge container (`--{position}`
//    modifier), the conditionally-rendered desktop copy elements, and the
//    ALWAYS-rendered mobile copy block whose *_mobile-else-desktop fallbacks
//    arrive server-resolved. Title HTML is |richtext-expanded (rich-text div
//    INSIDE the rich-text-classed wrapper).
// 2. The `body` Toolbox stream is a whole-field render, so every top-level
//    block keeps its Wagtail `w-block-{type} block-{type}` wrapper
//    (BasicPage/HomePage precedent), dispatched through buildToolboxPageMap
//    with the merged side-channels.
// 3. `.product-lists`: the anchor nav (`.product-list-nav__list` must be in
//    the initial DOM -- ui/product-list.ts one-shot-queries it on
//    DOMContentLoaded to mount Flickity; no React handlers added), one
//    `.product-list` section per category (title doubles as the raw DOM
//    id/anchor; product-card grid renders only when the sorted set is
//    non-empty), and the `.product-list-callout` over `extra_copy` -- a
//    bound-block loop (NO w-block wrappers, Fragment mode) where
//    `link_banner` renders the bespoke banner DOM (with the server-gettext'd
//    CTA label) instead of its LinkImageBlock island, and the
//    heading/paragraph/image/html primitives render as Wagtail render_basic
//    (BasicPage precedent). `value_prop_bar` is ExtraCopyBlocks' own key for
//    the shared ValuePropBarBlock (distinct from Toolbox's
//    `value_prop_bar_block`).
import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { LinkImageBlock as LinkImageBlockValue } from "@reactivated";
import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductCard } from "../components/ProductCard";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.ProductListPageTemplate;
type ILinkBannerValue = LinkImageBlockValue;

const Hero = (props: {
    hero: NonNullable<IProps["hero"]>;
}): React.JSX.Element => {
    const { hero } = props;
    return (
        <section
            className="hero product-list-hero product-list-hero--shop-pillows-hero l-full-width"
            style={
                hero.background_image_url
                    ? {
                          backgroundImage: `url(${hero.background_image_url})`,
                      }
                    : undefined
            }
        >
            <div
                className="product-list-hero__overlay product-list-hero__overlay--shop-pillows-hero"
                aria-hidden="true"
            ></div>
            <div className="l-capped-width product-list-hero__inner">
                {hero.has_badge && (
                    <div className="product-list-hero-badge">
                        <div className="product-list-hero-badge__image-container product-list-hero-badge__image-container--pillows">
                            <div
                                className={
                                    hero.badge_position
                                        ? `product-list-hero-badge__image-container-inner product-list-hero-badge__image-container-inner--${hero.badge_position}`
                                        : "product-list-hero-badge__image-container-inner"
                                }
                            >
                                {hero.badge_image_url && (
                                    <img
                                        alt={hero.badge_image_alt}
                                        src={hero.badge_image_url}
                                        className="product-list-hero-badge__image product-list-hero-badge__image--pillows responsive-img"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="product-list-hero__content product-list-hero__content--pillows">
                    {hero.pre_title && (
                        <h6 className="product-list-hero__category product-list-hero__category--pillows">
                            {hero.pre_title}
                        </h6>
                    )}
                    {hero.title_html && (
                        <div
                            className="product-list-hero__title product-list-hero__title--pillows rich-text"
                            dangerouslySetInnerHTML={{
                                __html: hero.title_html,
                            }}
                        />
                    )}
                    {hero.description && (
                        <p className="product-list-hero__description product-list-hero__description--pillows">
                            {hero.description}
                        </p>
                    )}
                </div>
                <div className="product-list-hero__content product-list-hero__content--pillows product-list-hero__content--pillows-mobile">
                    <h6 className="product-list-hero__category product-list-hero__category--pillows">
                        {hero.mobile_pre_title}
                    </h6>
                    <div
                        className="product-list-hero__title product-list-hero__title--pillows rich-text"
                        dangerouslySetInnerHTML={{
                            __html: hero.mobile_title_html,
                        }}
                    />
                    <p className="product-list-hero__description product-list-hero__description--pillows">
                        {hero.mobile_description}
                    </p>
                </div>
            </div>
        </section>
    );
};

const Section = (props: {
    section: IProps["sections"][number];
}): React.JSX.Element => {
    const { section } = props;
    return (
        <section className="product-list" id={section.title}>
            <div className="product-list__background l-capped-width">
                <div className="product-list__header">
                    <div className="product-list__header-copy">
                        <h2 className="product-list__title">{section.title}</h2>
                        <div
                            className="product-list__description"
                            dangerouslySetInnerHTML={{
                                __html: section.description_html,
                            }}
                        />
                    </div>
                    {section.header_image_url && (
                        <img
                            alt={section.header_image_alt}
                            src={section.header_image_url}
                            className="product-list__header-image responsive-img"
                            loading="lazy"
                            decoding="async"
                        />
                    )}
                </div>
                {section.cards.length > 0 && (
                    <div className="product-list__content u-flex-container">
                        {section.cards.map((card) => (
                            <ProductCard
                                key={card.product_id}
                                card={card}
                                cssClass="product-list__card"
                                auxCopy={true}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const toolboxMap = buildToolboxPageMap(props);
    // Flickity carousel on the anchor-nav list; watchCSS gates activation to the
    // mobile breakpoint.
    const navListRef = React.useRef<HTMLUListElement>(null);
    useFlickity(navListRef, {
        module: "flickity",
        options: {
            watchCSS: true,
            draggable: true,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: true,
        },
    });
    // The extra_copy callout map: the Toolbox keys (covering any Toolbox block
    // an editor places there), the four primitives as Wagtail render_basic,
    // the value_prop_bar alias, and the page's bespoke link_banner branch.
    const calloutMap = {
        ...toolboxMap,
        heading: (block: { value: Immutable<CharBlockValue> }) => (
            <>{block.value}</>
        ),
        paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ),
        image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
            <img
                src={block.value.url}
                alt={block.value.default_alt_text}
                width={block.value.width}
                height={block.value.height}
                loading="lazy"
                decoding="async"
            />
        ),
        html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),
        value_prop_bar: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,
        link_banner: (block: { value: Immutable<ILinkBannerValue> }) => (
            <div className="product-list-link-banner">
                {block.value.image && (
                    <img
                        className="product-list-link-banner__image responsive-img"
                        alt={block.value.image.title}
                        src={
                            block.value.image.renditions["original"]?.url ??
                            block.value.image.url
                        }
                        loading="lazy"
                        decoding="async"
                    />
                )}
                <div className="product-list-link-banner__copy l-capped-width">
                    <div className="product-list-link-banner__title">
                        {block.value.text}
                    </div>
                    {block.value.link && (
                        <a
                            href={block.value.link.url}
                            className="button product-list-link-banner__button"
                        >
                            {props.link_banner_cta_label}
                        </a>
                    )}
                </div>
            </div>
        ),
    };
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {props.hero && <Hero hero={props.hero} />}
            {(props.page.body?.value ?? []).map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={toolboxMap}
                />
            ))}
            <div className="product-lists u-margin-bottom--xl">
                <nav className="product-list-nav">
                    <ul
                        className="product-list-nav__list l-capped-width"
                        ref={navListRef}
                    >
                        {props.sections.map((section, i) => (
                            <li key={i} className="product-list-nav__item">
                                <a
                                    href={`#${section.title}`}
                                    className="product-list-nav__link"
                                >
                                    {section.title}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
                {props.sections.map((section, i) => (
                    <Section key={i} section={section} />
                ))}
                <div className="product-list-callout u-margin-bottom--xl">
                    {props.page.extra_copy && (
                        <StreamField
                            tagName={React.Fragment}
                            value={props.page.extra_copy.value}
                            components={calloutMap}
                        />
                    )}
                </div>
            </div>
        </PageSkeleton>
    );
};
